import React, { useState, useRef, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Card } from './Card';
import { CardType, Column } from './KanbanBoard';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';

interface KanbanBoardProps {
  columns: Column[];
  cards: { [cardId: string]: CardType };
  onAddCard: (columnId: string) => void;
  onMoveCard: (result: DropResult) => void;
  onEditColumn: (columnId: string, newTitle: string) => void;
  onDeleteColumn: (columnId: string) => void;
  onAddColumn: () => void;
  stepState?: Record<string, { checked: boolean; note: string }>;
  onStepCheck?: (cardId: string, checked: boolean) => void;
  onStepNoteChange?: (cardId: string, note: string) => void;
  onDeleteCard?: (colId: string, cardId: string) => void;
  onEditCard?: (colId: string, cardId: string) => void;
}

export const KanbanBoard: React.FC<KanbanBoardProps> = ({
  columns,
  cards,
  onAddCard,
  onMoveCard,
  onEditColumn,
  onDeleteColumn,
  onAddColumn,
  stepState = {},
  onStepCheck,
  onStepNoteChange,
  onDeleteCard,
  onEditCard,
}) => {
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const ZOOM_MIN = 0.3;
  const ZOOM_MAX = 2.5;
  const ZOOM_STEP = 0.15;

  // Initialize zoom based on screen size and center the board
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      setZoom(0.3); // Start at 30% zoom on mobile for better visibility
    }
    
    // Center the board after a short delay to ensure DOM is ready
    setTimeout(() => {
      centerBoard();
    }, 100);
  }, []);

  // Center the board function
  const centerBoard = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollWidth = container.scrollWidth;
      const scrollHeight = container.scrollHeight;
      const clientWidth = container.clientWidth;
      const clientHeight = container.clientHeight;
      
      // Center horizontally and vertically
      const centerX = Math.max(0, (scrollWidth - clientWidth) / 2);
      const centerY = Math.max(0, (scrollHeight - clientHeight) / 2);
      
      container.scrollTo({
        left: centerX,
        top: centerY,
        behavior: 'smooth'
      });
    }
  };

  // Handle wheel zoom
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
        setZoom(prev => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, prev + delta)));
      }
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, []);

  // Handle mouse events for panning
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button === 1 || (e.button === 0 && e.altKey)) { // Middle click or Alt+Left click
        e.preventDefault();
        setIsPanning(true);
        setLastPanPoint({ x: e.clientX, y: e.clientY });
        container.style.cursor = 'grabbing';
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (isPanning) {
        e.preventDefault();
        const deltaX = e.clientX - lastPanPoint.x;
        const deltaY = e.clientY - lastPanPoint.y;
        
        container.scrollBy({
          left: -deltaX,
          top: -deltaY,
          behavior: 'auto'
        });
        
        setLastPanPoint({ x: e.clientX, y: e.clientY });
      }
    };

    const handleMouseUp = () => {
      setIsPanning(false);
      container.style.cursor = 'grab';
    };

    const handleMouseLeave = () => {
      setIsPanning(false);
      container.style.cursor = 'grab';
    };

    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isPanning, lastPanPoint]);

  const handleZoomIn = () => setZoom(prev => Math.min(ZOOM_MAX, prev + ZOOM_STEP));
  const handleZoomOut = () => setZoom(prev => Math.max(ZOOM_MIN, prev - ZOOM_STEP));
  const handleZoomReset = () => {
    const newZoom = window.innerWidth < 768 ? 0.3 : 1;
    setZoom(newZoom);
    setTimeout(() => centerBoard(), 100);
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div className="relative w-full h-full bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl overflow-hidden">
      {/* Mobile Menu Toggle */}
      {isMobile && (
        <div className="absolute top-4 left-4 z-20">
          <button 
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="w-10 h-10 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-lg shadow-lg border border-slate-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      )}

      {/* Mobile Menu */}
      {isMobile && showMobileMenu && (
        <div className="absolute top-16 left-4 z-20 bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border border-slate-200 p-4 min-w-[200px]">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <button 
                onClick={handleZoomOut}
                className="w-8 h-8 flex items-center justify-center rounded-md bg-slate-100 hover:bg-slate-200 transition-colors text-slate-600"
                disabled={zoom <= ZOOM_MIN}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              <span className="text-sm font-mono text-slate-600 min-w-[3rem] text-center">
                {(zoom * 100).toFixed(0)}%
              </span>
              <button 
                onClick={handleZoomIn}
                className="w-8 h-8 flex items-center justify-center rounded-md bg-slate-100 hover:bg-slate-200 transition-colors text-slate-600"
                disabled={zoom >= ZOOM_MAX}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
            <button 
              onClick={handleZoomReset}
              className="w-full px-3 py-2 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors"
            >
              Reset Zoom
            </button>
            <button 
              onClick={onAddColumn}
              className="w-full px-3 py-2 text-xs bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded transition-colors"
            >
              Add Column
            </button>
          </div>
        </div>
      )}

      {/* Desktop Zoom Controls */}
      {!isMobile && (
        <div className="absolute top-4 right-4 z-20 flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-lg p-2 shadow-lg border border-slate-200">
          <button 
            onClick={handleZoomOut}
            className="w-8 h-8 flex items-center justify-center rounded-md bg-slate-100 hover:bg-slate-200 transition-colors text-slate-600 hover:text-slate-800"
            disabled={zoom <= ZOOM_MIN}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>
          
          <div className="flex items-center gap-1 px-2">
            <span className="text-sm font-mono text-slate-600 min-w-[3rem] text-center">
              {(zoom * 100).toFixed(0)}%
            </span>
          </div>
          
          <button 
            onClick={handleZoomIn}
            className="w-8 h-8 flex items-center justify-center rounded-md bg-slate-100 hover:bg-slate-200 transition-colors text-slate-600 hover:text-slate-800"
            disabled={zoom >= ZOOM_MAX}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
          
          <div className="w-px h-6 bg-slate-300 mx-1"></div>
          
          <button 
            onClick={handleZoomReset}
            className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors"
          >
            Reset
          </button>
        </div>
      )}

      {/* Pan Instructions */}
      {!isMobile && (
        <div className="absolute bottom-4 left-4 z-20 bg-white/90 backdrop-blur-sm rounded-lg p-2 shadow-lg border border-slate-200 text-xs text-slate-600">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
            </svg>
            <span>Alt+Click or Middle Click to pan</span>
          </div>
        </div>
      )}

      {/* Scrollable Board Container */}
      <div 
        ref={scrollContainerRef}
        className={`w-full h-full overflow-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100 ${!isMobile ? 'cursor-grab active:cursor-grabbing' : ''}`}
        style={{ 
          height: isMobile ? 'calc(100vh - 120px)' : 'calc(100vh - 240px)',
          scrollbarWidth: 'thin',
          scrollbarColor: '#cbd5e1 #f1f5f9',
          maxWidth: '100%',
          maxHeight: '100%'
        }}
      >
        <div 
          className={`${isMobile ? 'p-2' : 'p-6'} min-w-max ${isMobile ? 'pb-16' : ''}`}
          style={{ 
            transform: `scale(${zoom})`, 
            transformOrigin: 'top left', 
            transition: isDragging ? 'none' : 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            minHeight: '100%',
            minWidth: 'max-content',
            maxWidth: 'none'
          }}
        >
          {/* Add Column Button - Desktop Only */}
          {!isMobile && (
            <div className="mb-6">
              <button 
                onClick={onAddColumn}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Column
              </button>
            </div>
          )}

          <DragDropContext 
            onDragEnd={(result) => {
              onMoveCard(result);
              setIsDragging(false);
            }}
            onDragStart={() => setIsDragging(true)}
          >
            <div className={`flex gap-6 items-start ${isMobile ? 'flex-col' : ''}`}>
              {columns.map((col, colIdx) => (
                <Droppable droppableId={col.id} key={col.id}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`
                        ${isMobile ? 'w-full' : 'flex-shrink-0 w-80'} bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-slate-200/50
                        transition-all duration-200 hover:shadow-xl hover:bg-white/90
                        ${snapshot.isDraggingOver ? 'ring-2 ring-blue-400 ring-opacity-50 bg-blue-50/50' : ''}
                        ${isDragging ? 'transform scale-105' : ''}
                      `}
                      style={{ 
                        minHeight: isMobile ? '150px' : '400px',
                        maxHeight: isMobile ? '250px' : 'calc(100vh - 300px)',
                        overflow: 'hidden'
                      }}
                    >
                      {/* Column Header */}
                      <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-200">
                        <div className="flex items-center gap-2 flex-1">
                          <input
                            className="font-bold text-lg bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-blue-400 rounded px-2 py-1 transition-all text-slate-800"
                            value={col.title}
                            onChange={e => onEditColumn(col.id, e.target.value)}
                            placeholder="Column title..."
                          />
                          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">
                            {col.cardIds.length}
                          </span>
                        </div>
                        <button 
                          onClick={() => onDeleteColumn(col.id)}
                          className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                          title="Delete column"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>

                      {/* Extra Header Content */}
                      {col.extraHeader && (
                        <div className="mb-3">
                          {col.extraHeader}
                        </div>
                      )}

                      {/* Cards Container */}
                      <div className="flex-1 space-y-3 overflow-y-auto" style={{ maxHeight: isMobile ? '100px' : 'calc(100vh - 400px)' }}>
                        {col.cardIds.map((cardId, idx) => {
                          const card = cards[cardId];
                          const isStep = card?.tags?.includes('methodology');
                          
                          if (!card) return null;
                          
                          return (
                            <Draggable draggableId={cardId} index={idx} key={cardId}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={`
                                    transform transition-all duration-200
                                    ${snapshot.isDragging ? 'rotate-3 scale-105 shadow-2xl z-50' : 'hover:scale-[1.02]'}
                                  `}
                                >
                                  <div
                                    className={`
                                      bg-white rounded-lg shadow-sm border border-slate-200 cursor-pointer
                                      transition-all duration-200 hover:shadow-md hover:border-slate-300
                                      ${snapshot.isDragging ? 'shadow-2xl' : ''}
                                    `}
                                    style={{ 
                                      borderLeft: `4px solid ${card.color || '#64748b'}`,
                                      minHeight: isMobile ? '60px' : '80px'
                                    }}
                                    onClick={e => {
                                      if ((e.target as HTMLElement).closest('button')) return;
                                      onEditCard?.(col.id, card.id);
                                    }}
                                  >
                                    <div className="p-3">
                                      {/* Card Header */}
                                      <div className="flex items-start justify-between gap-2 mb-2">
                                        <h3 className={`font-semibold text-slate-800 leading-tight flex-1 ${isMobile ? 'text-sm' : 'text-sm'}`}>
                                          {card.title}
                                        </h3>
                                        <button
                                          className="text-slate-400 hover:text-red-500 p-1 rounded transition-colors opacity-0 group-hover:opacity-100"
                                          onClick={ev => { 
                                            ev.stopPropagation(); 
                                            onDeleteCard?.(col.id, card.id); 
                                          }}
                                          title="Delete card"
                                        >
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                          </svg>
                                        </button>
                                      </div>

                                      {/* Card Type Badge */}
                                      <div className="mb-2">
                                        <span className={`
                                          inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium
                                          ${card.type === 'vuln' ? 'bg-red-100 text-red-700' : 
                                            card.type === 'tool' ? 'bg-blue-100 text-blue-700' : 
                                            card.type === 'payload' ? 'bg-orange-100 text-orange-700' : 
                                            card.type === 'resource' ? 'bg-green-100 text-green-700' : 
                                            'bg-slate-100 text-slate-700'}
                                        `}>
                                          {card.type === 'vuln' ? '🐞' : 
                                           card.type === 'tool' ? '🔧' : 
                                           card.type === 'payload' ? '💥' : 
                                           card.type === 'resource' ? '🔗' : '📝'}
                                          {card.type === 'vuln' ? 'Vulnerability' : 
                                           card.type === 'tool' ? 'Tool' : 
                                           card.type === 'payload' ? 'Payload' : 
                                           card.type === 'resource' ? 'Resource' : 'Note'}
                                        </span>
                                      </div>

                                      {/* Severity Badge for Vulnerabilities */}
                                      {card.type === 'vuln' && card.content?.severity && (
                                        <div className="mb-2">
                                          <span className={`
                                            inline-block px-2 py-1 rounded text-xs font-bold text-white
                                            ${card.content.severity === 'Critical' ? 'bg-red-600' : 
                                              card.content.severity === 'High' ? 'bg-orange-500' : 
                                              card.content.severity === 'Medium' ? 'bg-yellow-400 text-slate-900' : 
                                              'bg-green-500'}
                                          `}>
                                            {card.content.severity}
                                          </span>
                                        </div>
                                      )}

                                      {/* Tags - Hidden on mobile to save space */}
                                      {!isMobile && card.tags && card.tags.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mb-2">
                                          {card.tags.map(tag => (
                                            <span key={tag} className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-xs font-medium">
                                              #{tag}
                                            </span>
                                          ))}
                                        </div>
                                      )}

                                      {/* Content Preview - Condensed on mobile */}
                                      <div className="text-xs text-slate-600 leading-relaxed">
                                        {card.type === 'note' && (
                                          <div className={isMobile ? "line-clamp-2" : "line-clamp-3"}>
                                            <div className="text-xs whitespace-pre-wrap">
                                              <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{typeof card.content === 'string' ? card.content : card.content?.note || ''}</ReactMarkdown>
                                            </div>
                                          </div>
                                        )}
                                        {card.type === 'vuln' && (
                                          <>
                                            {card.content?.description && (
                                              <div className={isMobile ? "line-clamp-1 mb-1" : "line-clamp-2 mb-1"}>
                                                <div className="text-xs whitespace-pre-wrap">
                                                  <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{card.content.description}</ReactMarkdown>
                                                </div>
                                              </div>
                                            )}
                                            {card.content?.poc && !isMobile && (
                                              <div className="line-clamp-2">
                                                <div className="text-xs whitespace-pre-wrap">
                                                  <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{card.content.poc}</ReactMarkdown>
                                                </div>
                                              </div>
                                            )}
                                          </>
                                        )}
                                        {card.type === 'tool' && card.content?.command && (
                                          <div className="bg-slate-50 p-2 rounded text-xs font-mono line-clamp-2">
                                            {card.content.command}
                                          </div>
                                        )}
                                        {card.type === 'payload' && card.content?.code && (
                                          <div className="bg-slate-50 p-2 rounded text-xs font-mono line-clamp-2">
                                            {card.content.code}
                                          </div>
                                        )}
                                      </div>

                                      {/* Custom Fields - Hidden on mobile */}
                                      {!isMobile && Array.isArray(card.content?.customFields) && card.content.customFields.length > 0 && (
                                        <div className="mt-2 pt-2 border-t border-slate-100">
                                          {card.content.customFields.map((f: any, i: number) => (
                                            <div key={i} className="flex gap-2 text-xs mb-1">
                                              <span className="font-medium text-slate-500">{f.key}:</span>
                                              <span className="text-slate-700">{f.value}</span>
                                            </div>
                                          ))}
                                        </div>
                                      )}

                                      {/* Step Controls - Condensed on mobile */}
                                      {isStep && (
                                        <div className="mt-3 pt-2 border-t border-slate-100">
                                          <div className="flex items-center gap-2">
                                            <input 
                                              type="checkbox" 
                                              checked={stepState[cardId]?.checked} 
                                              onChange={e => onStepCheck?.(cardId, e.target.checked)}
                                              className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                            />
                                            <input 
                                              type="text" 
                                              className="flex-1 text-xs border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400" 
                                              value={stepState[cardId]?.note || ''} 
                                              onChange={e => onStepNoteChange?.(cardId, e.target.value)} 
                                              placeholder="Add note..." 
                                            />
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>

                      {/* Add Card Button */}
                      <div className="mt-4 pt-3 border-t border-slate-200">
                        <button 
                          onClick={() => onAddCard(col.id)}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-lg border border-dashed border-slate-300 hover:border-slate-400 transition-all duration-200 text-sm font-medium"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add Card
                        </button>
                      </div>
                    </div>
                  )}
                </Droppable>
              ))}
            </div>
          </DragDropContext>
        </div>
      </div>
    </div>
  );
}; 